import { useQuery } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

export const MEETING_PREP_TEMPLATES_QUERY_KEY = ['meetingPrep', 'templates'];

/** A user has at most one template, so an empty list means never configured rather than an error. */
export const useMeetingPrepTemplatesQuery = ({
	enabled = true,
	meta,
}: {
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
} = {}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: () => userSession.webServiceHelper.callAsync<Api.MeetingPrepTemplate[]>('meetingPrep/templates', 'GET'),
		queryKey: MEETING_PREP_TEMPLATES_QUERY_KEY,
		meta,
	});
};

export const invalidateMeetingPrepTemplates = () =>
	reactQueryClient.invalidateQueries({ queryKey: MEETING_PREP_TEMPLATES_QUERY_KEY });

/**
 * The server's partial unique index makes a second template per user impossible, so the sort only
 * matters for a list that predates it — newest wins.
 */
export const selectBoundMeetingPrepTemplate = (
	templates: Api.MeetingPrepTemplate[]
): Api.MeetingPrepTemplate | undefined => {
	if (templates.length <= 1) {
		return templates[0];
	}
	return [...templates].sort((a, b) => (b.lastModifiedDate ?? '').localeCompare(a.lastModifiedDate ?? ''))[0];
};

export const updateMeetingPrepTemplatesRQCache = (updatedTemplate: Api.MeetingPrepTemplate) => {
	reactQueryClient.setQueryData<Api.MeetingPrepTemplate[]>(MEETING_PREP_TEMPLATES_QUERY_KEY, oldTemplates => {
		if (!oldTemplates) {
			return [updatedTemplate];
		}
		const index = oldTemplates.findIndex(t => t.id === updatedTemplate.id);
		if (index === -1) {
			return [...oldTemplates, updatedTemplate];
		}
		const newTemplates = [...oldTemplates];
		newTemplates[index] = updatedTemplate;
		return newTemplates;
	});
};
